import { css } from '@emotion/core';
import { labelStyles } from '@zapier/style-encapsulation';
import type { ColorValue } from '../../../theme';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import type { Props, Configs } from './Alert';

export const typeConfigs = (isRebrand: boolean): Configs => {
  return {
    info: {
      backgroundColor: 'ocean',
      color: 'white',
      icon: isRebrand ? 'alertInfoFill' : 'alertInfo',
    },
    success: {
      backgroundColor: 'success',
      color: 'gray',
      icon: 'formCheckCircle',
    },
    warning: {
      backgroundColor: 'warning',
      color: 'gray',
      icon: isRebrand ? 'alertTriangleFill' : 'alertCircle',
    },
    error: {
      backgroundColor: 'error',
      color: 'white',
      icon: isRebrand ? 'formXCircle' : 'alertTriangle',
    },
  };
};

const mapV4ToV5Color = (color: ColorValue): ColorValue => {
  let transformedColor: ColorValue;
  switch (color) {
    case Colors.gray:
      transformedColor = Colors.neutral700;
      break;
    case Colors.ocean:
      transformedColor = Colors.blue;
      break;
    case Colors.white:
      transformedColor = Colors.neutral100;
      break;
    case Colors.success:
      transformedColor = Colors.success400;
      break;
    case Colors.warning:
      transformedColor = Colors.warning400;
      break;
    case Colors.error:
      transformedColor = Colors.error400;
      break;
    default:
      transformedColor = Colors.neutral700;
  }
  return transformedColor;
};

const Overrides = {
  root: (props: Props, isRebrand: boolean) => [
    props.layout === 'box' && [
      css`
        border: 1px solid ${Colors.neutral300};
        border-radius: 5px;
        background-color: ${Colors.neutral100};
        color: ${Colors.neutral700};
      `,
      css`
        &::before {
          background-color: ${mapV4ToV5Color(
            Colors[typeConfigs(isRebrand)[props.type!].backgroundColor]
          )};
        }
      `,
    ],
    props.layout === 'banner' && [
      css`
        ${Typography.paragraph3};
      `,
      css`
        color: ${mapV4ToV5Color(
          Colors[typeConfigs(isRebrand)[props.type!].color]
        )};
        background-color: ${mapV4ToV5Color(
          Colors[typeConfigs(isRebrand)[props.type!].backgroundColor]
        )};
      `,
    ],
    props.layout === 'banner-inverse' && [
      css`
        ${Typography.paragraph3};
        background-color: ${Colors.neutral100};
        color: ${Colors.neutral700};
      `,
    ],
    props.layout === 'modal' && [
      css`
        background-color: ${Colors.neutral100};
        border: 1px solid ${Colors.neutral300};
      `,
    ],
  ],
  typeIcon: (props: Props, isRebrand: boolean) => [
    css`
      color: ${mapV4ToV5Color(
        Colors[typeConfigs(isRebrand)[props.type!].backgroundColor]
      )};
    `,
    props.layout === 'modal' &&
      css`
        color: ${mapV4ToV5Color(
          Colors[typeConfigs(isRebrand)[props.type!].backgroundColor]
        )};
      `,
    props.layout === 'banner' &&
      css`
        color: ${Colors.neutral100};
        // Rare tag selection to ensure that links are accessible
        // and consumers don't need to worry about it.
        a[class] {
          &:hover,
          &:focus {
            color: ${Colors.neutral500};
          }
          &:focus {
            outline: 1px solid ${Colors.neutral500};
          }
        }
      `,
  ],
  titleInner: (props: Props) => [
    css`
      ${Typography.paragraph3Bold};
      color: ${Colors.neutral800};
    `,
    props.layout === 'banner' &&
      css`
        color: inherit;
      `,
  ],
  closeButton: (props: Props) => [
    props.layout !== 'banner' &&
      css`
        color: ${Colors.neutral600};
        &:hover,
        &:focus {
          color: ${Colors.neutral600};
        }
      `,
  ],
};

export const Styles = labelStyles('Alert', {
  root: (props: Props, isRebrand: boolean) => [
    props.layout === 'box' && [
      css`
        label: -box;
        position: relative;
        display: grid;
        grid-template-columns: max-content 1fr max-content;
        align-items: start;
        gap: 10px;
        padding: 10px 10px 10px 20px;
        border: 1px solid ${Colors.gray20};
        border-radius: 10px;
        background-color: ${Colors.white};
        ${Typography.paragraph3};
        color: ${Colors.gray};

        // Colored stripe that renders on the left of the box.
        &::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          border-top-left-radius: inherit;
          border-bottom-left-radius: inherit;
          width: 10px;
        }
      `,
      css`
        label: -${props.type};
        &::before {
          background-color: ${Colors[
            typeConfigs(isRebrand)[props.type!].backgroundColor
          ]};
        }
      `,
    ],
    props.layout === 'banner' && [
      css`
        label: -banner;
        position: relative;
        display: grid;
        align-items: center;
        ${Typography.smallPrint1};
        padding: 10px 20px;
        text-align: center;
      `,
      css`
        label: -${props.type};
        color: ${Colors[typeConfigs(isRebrand)[props.type!].color]};
        background-color: ${Colors[
          typeConfigs(isRebrand)[props.type!].backgroundColor
        ]};

        // Rare tag selection to ensure that links are accessible
        // and consumers don't need to worry about it.
        a[class] {
          color: inherit;
          text-decoration: underline;
          &:hover,
          &:focus {
            color: ${Colors.gray40};
          }
          &:focus {
            outline: 1px solid ${Colors.gray40};
          }
        }
      `,
    ],
    props.layout === 'banner-inverse' && [
      css`
        label: -banner;
        position: relative;
        display: grid;
        align-items: center;
        ${Typography.smallPrint1};
        padding: 10px 20px;
        text-align: center;
      `,
      css`
        label: -${props.type};
        color: ${Colors.gray};
        background-color: ${Colors.white};
      `,
    ],
    props.layout === 'modal' && [
      css`
        label: -modal;
        position: relative;
        max-width: 500px;
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: ${Colors.white};
        padding: 15px;
        border: 1px solid ${Colors.gray20};
        text-align: center;
        border-radius: 10px;
      `,
    ],
    props.hasShadow &&
      css`
        label: -shadowed;
        box-shadow: ${Shadows.elevation5};
      `,
    isRebrand && [...Overrides.root(props, isRebrand)],
  ],
  typeIcon: (props: Props, isRebrand: boolean) => [
    css`
      color: ${Colors[typeConfigs(isRebrand)[props.type!].backgroundColor]};
    `,
    props.layout === 'modal' &&
      css`
        margin-bottom: 8px;
      `,
    (props.layout === 'banner' || props.layout === 'banner-inverse') &&
      css`
        color: ${props.layout === 'banner'
          ? 'currentColor'
          : Colors[typeConfigs(isRebrand)[props.type!].backgroundColor]};
        display: inline-block;
        vertical-align: middle;
        margin-right: 5px;
        position: relative;
        top: -2px;
      `,
    isRebrand && [...Overrides.typeIcon(props, isRebrand)],
  ],
  titleInner: (props: Props, isRebrand: boolean) => [
    css`
      display: block;
      font-weight: 700;
    `,
    isRebrand && Overrides.titleInner(props),
  ],
  closeButton: (props: Props, isRebrand: boolean) => [
    props.layout !== 'banner' &&
      css`
        color: ${Colors.gray60};
        transition: ${Animation.transitionValue};

        &:hover,
        &:focus {
          color: ${Colors.gray};
        }
      `,
    props.layout === 'banner' &&
      css`
        color: inherit;
      `,
    (props.layout === 'banner' || props.layout === 'banner-inverse') &&
      css`
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
      `,
    props.layout === 'modal' &&
      css`
        position: absolute;
        top: 15px;
        right: 15px;
      `,
    isRebrand && [...Overrides.closeButton(props)],
  ],
});
